import { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Layers, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  manifest,
  defaultUrlFor,
  isTabbed,
  type ManifestDoc,
  type ManifestGroup,
} from '../config/manifest';
import SiteHeader from './SiteHeader';
import NotApproved from './NotApproved';

const SearchModal = lazy(() => import('./SearchModal'));

export default function HomePage() {
  const {
    user, login, authError, loading, isAuthorized, profileLoading,
    emailLinkState, sendLink, submitEmailForLink,
  } = useAuth();
  // Search needs a token AND allowlist authorization (the index endpoint
  // rejects unapproved users). Gate the affordance — button, ⌘K, modal — on the
  // same condition the gated CTAs use, so unapproved / mid-magic-link visitors
  // never see a search box that would 401/403. Mirrors DocLayout (which only
  // mounts behind RequireAuth → isAuthorized===true).
  const canSearch =
    isAuthorized === true &&
    emailLinkState !== 'processing' &&
    emailLinkState !== 'needs_email';
  const [searchOpen, setSearchOpen] = useState(false);

  // Reset open state if the gate drops (e.g. sign-out / authorization revoked
  // while the modal is open) so it can't silently auto-reopen on re-auth.
  useEffect(() => {
    if (!canSearch) setSearchOpen(false);
  }, [canSearch]);

  useEffect(() => {
    if (!canSearch) return;
    function onKey(e: KeyboardEvent) {
      const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
      const cmdK = (isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (cmdK) {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canSearch]);

  function renderPanel() {
    // Magic-link completion takes precedence over everything (it may sign in a
    // different user; never flash the previous user's authorized UI).
    if (emailLinkState === 'processing') return <Status>Signing you in…</Status>;
    if (emailLinkState === 'needs_email') return <EmailLinkPrompt onSubmit={submitEmailForLink} />;
    if (!user) {
      return (
        <SignInPanel
          onLogin={login}
          sendLink={sendLink}
          error={authError}
          loading={loading}
          linkError={emailLinkState === 'error'}
        />
      );
    }
    // Signed in — but only render gated content once authorization is confirmed.
    if (profileLoading || isAuthorized === null) return <Status>Verifying access…</Status>;
    if (isAuthorized === false) return <NotApproved />;
    return (
      <div className="home__groups">
        {manifest.groups.map((group) => (
          <GroupSection key={group.id} group={group} />
        ))}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper-50">
      <SiteHeader onSearchClick={canSearch ? () => setSearchOpen(true) : undefined} />
      <main className="home">
        <section className="home__hero">
          <p className="home__eyebrow">{manifest.site.title}</p>
          <h1 className="home__title">
            <span className="wordmark">Henoyo</span>
          </h1>
          <p className="home__tagline">
            Governed AI in your cloud — for security teams who need proof, not more noise.
          </p>
          <p className="home__lede">{manifest.site.tagline}</p>
          {/* Only show the doc/deck CTAs to authorized users (and never mid
              magic-link flow) — they bounce everyone else back to sign-in /
              not-approved anyway. isAuthorized===true already implies the profile
              fetch settled (fetchProfile sets isAuthorized null while loading). */}
          {isAuthorized === true && emailLinkState !== 'processing' && emailLinkState !== 'needs_email' && (
            <div className="home__cta-row">
              <Link to="/d/welcome/about-henoyo" className="home__cta">
                Start here · About Henoyo
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/decks/partner" className="home__cta home__cta--ghost">
                View the deck
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
        </section>

        {renderPanel()}
      </main>
      <footer className="home__footer">
        <span className="wordmark">Henoyo</span> · All rights reserved · {manifest.site.watermark}
      </footer>
      {canSearch && searchOpen && (
        <Suspense fallback={null}>
          <SearchModal onClose={() => setSearchOpen(false)} />
        </Suspense>
      )}
    </div>
  );
}

function Status({ children }: { children: React.ReactNode }) {
  return (
    <section className="home__signin" aria-live="polite">
      <p>{children}</p>
    </section>
  );
}

function SignInPanel({
  onLogin,
  sendLink,
  error,
  loading,
  linkError,
}: {
  onLogin: () => Promise<void>;
  sendLink: (email: string) => Promise<void>;
  error: string | null;
  loading: boolean;
  linkError: boolean;
}) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending || !email.trim()) return;
    setSending(true);
    setLocalErr(null);
    try {
      await sendLink(email);
      setSent(true);
    } catch {
      setLocalErr('Couldn’t send the link — check the email address and try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="home__signin">
      <h2>Sign in</h2>
      <p>
        This is a pre-release design-partner reference. Sign in with your approved
        email to continue. Only approved partner emails can access the docs.
      </p>
      {linkError && (
        <p className="home__signin-error">
          That sign-in link was invalid or already used. Request a new one below.
        </p>
      )}
      {error && <p className="home__signin-error">{error}</p>}

      {/* Email-first: a one-time sign-in link is the primary path. The Google
          fallback + note appear only before a link is requested, so the "sent"
          confirmation stands alone. */}
      {sent ? (
        <p className="home__signin-sent">
          Check your email — we sent a sign-in link to <strong>{email}</strong>. Open
          it on this device to finish signing in.
        </p>
      ) : (
        <>
          <form onSubmit={handleSend} className="home__signin-magic">
            <label htmlFor="signin-email" className="home__signin-label">Email address</label>
            <input
              id="signin-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              autoComplete="email"
              className="home__signin-input"
            />
            <button type="submit" disabled={sending} className="home__signin-btn">
              {sending ? 'Sending…' : 'Email me a sign-in link'}
            </button>
          </form>
          {localErr && <p className="home__signin-error">{localErr}</p>}
          <p className="home__signin-note">
            We’ll email a one-time link — works with any address your Henoyo contact has
            approved, no Google account needed.
          </p>

          <div className="home__signin-divider"><span>or</span></div>

          <button type="button" onClick={onLogin} disabled={loading} className="home__signin-btn home__signin-btn--ghost">
            Continue with Google
            <ArrowRight className="h-4 w-4" />
          </button>
        </>
      )}
    </section>
  );
}

function EmailLinkPrompt({ onSubmit }: { onSubmit: (email: string) => Promise<void> }) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting || !email.trim()) return;
    setSubmitting(true);
    await onSubmit(email);
    setSubmitting(false);
  };

  return (
    <section className="home__signin">
      <h2>Confirm your email</h2>
      <p>To finish signing in, enter the email address where you received the link.</p>
      <form onSubmit={handle} className="home__signin-magic">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          autoComplete="email"
          className="home__signin-input"
          aria-label="Email address"
        />
        <button type="submit" disabled={submitting} className="home__signin-btn">
          {submitting ? 'Signing in…' : 'Finish sign-in'}
        </button>
      </form>
    </section>
  );
}

function GroupSection({ group }: { group: ManifestGroup }) {
  return (
    <section className="home__group">
      <header className="home__group-head">
        <h2 className="home__group-title">{group.title}</h2>
        {group.subtitle && <p className="home__group-sub">{group.subtitle}</p>}
      </header>
      <div className="home__cards">
        {group.docs.map((doc) => (
          <DocCard key={doc.slug} group={group} doc={doc} />
        ))}
      </div>
    </section>
  );
}

function DocCard({ group, doc }: { group: ManifestGroup; doc: ManifestDoc }) {
  const tabbed = isTabbed(doc);
  const Icon = tabbed ? Layers : FileText;

  return (
    <Link to={defaultUrlFor(group.id, doc.slug)} className="card">
      <div className="card__head">
        <div className="card__icon">
          <Icon className="h-4 w-4" />
        </div>
        <h3 className="card__title">{doc.title}</h3>
        {doc.status && <span className={`status status--${doc.status}`}>{doc.status}</span>}
      </div>
      {doc.subtitle && <p className="card__sub">{doc.subtitle}</p>}
      <div className="card__meta">
        {tabbed && (
          <span className="card__tabs">
            {doc.tabs.length} {doc.tabs.length === 1 ? 'view' : 'views'}
          </span>
        )}
        {doc.owner && <span>Owner · {doc.owner}</span>}
        {doc.lastReviewed && <span>Reviewed · {doc.lastReviewed}</span>}
      </div>
    </Link>
  );
}
